import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ordersService } from '../services/orders';
import { productsService } from '../services/products';
import { 
  CheckCircleIcon, 
  PrinterIcon,
  EnvelopeIcon,
  TruckIcon,
  CreditCardIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

export const OrderConfirmationPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  // Récupérer les détails de la commande
  const { data: order, isLoading, error } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => ordersService.getOrder(parseInt(orderId!)),
    enabled: !!orderId,
  });

  if (isLoading) {
    return (
      <div className="container-custom py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container-custom py-12">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">
            Erreur lors du chargement de la commande
          </p>
          <button onClick={() => navigate('/')} className="btn-secondary">
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-custom py-12">
        {/* En-tête de confirmation */}
        <div className="text-center mb-8">
          <CheckCircleIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Commande confirmée !
          </h1>
          <p className="text-lg text-gray-600">
            Merci pour votre commande {ordersService.formatOrderNumber(order.number)}
          </p>
        </div>

        {/* Actions rapides */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <PrinterIcon className="h-5 w-5" />
            Imprimer
          </button>
          <a
            href={`mailto:?subject=Commande ${order.number}&body=Ma commande ${order.number} a été confirmée.`}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <EnvelopeIcon className="h-5 w-5" />
            Envoyer par email
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Détails de la commande */}
          <div className="lg:col-span-2 space-y-6">
            {/* Résumé de la commande */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4">Détails de la commande</h2>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-600">Numéro de commande</p>
                  <p className="font-semibold">{ordersService.formatOrderNumber(order.number)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="font-semibold">
                    {new Date(order.date_created).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Statut</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${ordersService.getOrderStatusColor(order.status)}`}>
                    {ordersService.getOrderStatusLabel(order.status)}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Mode de paiement</p>
                  <p className="font-semibold">{order.payment_method_title}</p>
                </div>
              </div>

              {/* Produits commandés */}
              <h3 className="font-semibold mb-3">Produits commandés</h3>
              <div className="space-y-3">
                {order.line_items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 pb-3 border-b last:border-0">
                    <div className="flex-1">
                      <p className="font-medium">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        {productsService.getFormattedPrice(item.price)} × {item.quantity}
                      </p>
                    </div>
                    <p className="font-medium">
                      {productsService.getFormattedPrice(parseFloat(item.total))}
                    </p>
                  </div>
                ))}
              </div>

              {/* Totaux */}
              <div className="mt-6 pt-4 border-t space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Sous-total</span>
                  <span>{productsService.getFormattedPrice(parseFloat(order.total) - parseFloat(order.shipping_total))}</span>
                </div>
                {order.shipping_lines.length > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Livraison ({order.shipping_lines[0].method_title})</span>
                    <span>{productsService.getFormattedPrice(parseFloat(order.shipping_total))}</span>
                  </div>
                )}
                {parseFloat(order.total_tax) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>TVA</span>
                    <span>{productsService.getFormattedPrice(parseFloat(order.total_tax))}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                  <span>Total</span>
                  <span>{productsService.getFormattedPrice(parseFloat(order.total))}</span>
                </div>
              </div>
            </div>

            {/* Instructions de paiement */}
            {order.payment_method === 'bacs' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <CreditCardIcon className="h-5 w-5" />
                  Instructions de paiement
                </h3>
                <p className="text-blue-800 mb-3">
                  Veuillez effectuer un virement bancaire sur notre compte :
                </p>
                <div className="bg-white rounded p-4 font-mono text-sm">
                  <p>IBAN : FR76 1234 5678 9012 3456 7890 123</p>
                  <p>BIC : BNPAFRPP</p>
                  <p>Référence : {order.number}</p>
                </div>
                <p className="text-sm text-blue-700 mt-3">
                  Votre commande sera traitée dès réception du paiement.
                </p>
              </div>
            )}

            {order.payment_method === 'cheque' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  Instructions de paiement
                </h3>
                <p className="text-blue-800 mb-3">
                  Veuillez envoyer votre chèque à l'ordre de "Votre Entreprise" à :
                </p>
                <div className="bg-white rounded p-4">
                  <p className="font-semibold">Votre Entreprise</p>
                  <p>123 Rue du Commerce</p>
                  <p>75001 Paris</p>
                  <p className="mt-2">Référence commande : {order.number}</p>
                </div>
                <p className="text-sm text-blue-700 mt-3">
                  Votre commande sera traitée dès réception du chèque.
                </p>
              </div>
            )}
          </div>

          {/* Informations de livraison */}
          <div className="lg:col-span-1 space-y-6">
            {/* Adresse de facturation */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CreditCardIcon className="h-5 w-5 text-gray-400" />
                Adresse de facturation
              </h3>
              <address className="not-italic text-sm text-gray-600">
                <p className="font-medium text-gray-900">
                  {order.billing.first_name} {order.billing.last_name}
                </p>
                {order.billing.company && <p>{order.billing.company}</p>}
                <p>{order.billing.address_1}</p>
                {order.billing.address_2 && <p>{order.billing.address_2}</p>}
                <p>{order.billing.postcode} {order.billing.city}</p>
                <p>{order.billing.country}</p>
                <p className="mt-2">
                  <a href={`mailto:${order.billing.email}`} className="text-primary-600 hover:underline">
                    {order.billing.email}
                  </a>
                </p>
                {order.billing.phone && <p>{order.billing.phone}</p>}
              </address>
            </div>

            {/* Adresse de livraison */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TruckIcon className="h-5 w-5 text-gray-400" />
                Adresse de livraison
              </h3>
              <address className="not-italic text-sm text-gray-600">
                <p className="font-medium text-gray-900">
                  {order.shipping.first_name} {order.shipping.last_name}
                </p>
                {order.shipping.company && <p>{order.shipping.company}</p>}
                <p>{order.shipping.address_1}</p>
                {order.shipping.address_2 && <p>{order.shipping.address_2}</p>}
                <p>{order.shipping.postcode} {order.shipping.city}</p>
                <p>{order.shipping.country}</p>
              </address>
            </div>

            {/* Note de commande */}
            {order.customer_note && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold mb-3">Note de commande</h3>
                <p className="text-sm text-gray-600">{order.customer_note}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions en bas de page */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Un email de confirmation a été envoyé à {order.billing.email}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products" className="btn-primary">
              Continuer vos achats
            </Link>
            <Link to="/account/orders" className="btn-secondary">
              Voir mes commandes
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

